/**
 * Cliente HTTP da API.
 *
 * Duas responsabilidades que não valem uma biblioteca inteira: anexar o access
 * token e renovar a sessão quando ele vence. O resto é `fetch`.
 */

const ACCESS_KEY = 'mpa.access';
const REFRESH_KEY = 'mpa.refresh';
const SUPORTE_KEY = 'mpa.suporte';

export interface ApiErro {
  status: number;
  mensagem: string;
  /** Erros de validação por campo, quando o backend os devolve (Zod). */
  campos?: Record<string, string>;
  /** O `code` da resposta, quando a tela precisa reagir a um erro específico. */
  codigo?: string;
}

export class ApiError extends Error implements ApiErro {
  constructor(
    readonly status: number,
    readonly mensagem: string,
    readonly campos?: Record<string, string>,
    readonly codigo?: string,
  ) {
    super(mensagem);
    this.name = 'ApiError';
  }
}

export interface Paginado<T> {
  data: T[];
  meta: { page: number; perPage: number; total: number; totalPages: number };
}

/**
 * Onde a sessão fica guardada — é isto que o "Lembrar de mim" decide.
 *
 * Marcado    → `localStorage`: sobrevive a fechar o navegador.
 * Desmarcado → `sessionStorage`: morre com a aba. É o padrão, e é o que faz
 *              sentido em computador compartilhado (recepção do evento, máquina
 *              emprestada), onde "sair" é algo que as pessoas esquecem.
 *
 * A renovação de token não recebe essa escolha e nem deveria: ela chama
 * `salvar` sem o terceiro argumento e a sessão continua onde já estava, em vez
 * de silenciosamente virar permanente no primeiro refresh.
 */
function lojaAtual(): Storage {
  return sessionStorage.getItem(REFRESH_KEY) ? sessionStorage : localStorage;
}

export interface SessaoSuporte {
  token: string;
  organizationId: string;
  organizationName: string;
  reason: string;
}

/**
 * Acesso de suporte do Superadmin dentro de uma organização.
 *
 * SEMPRE em `sessionStorage`, sem opção — ao contrário da sessão normal, que o
 * "Lembrar de mim" pode tornar persistente. Um acesso ao ambiente de um cliente
 * que sobrevive a fechar o navegador é o oposto do que a auditoria promete.
 *
 * Entrar e sair fazem recarga completa da página (ver `Clientes` e `AppLayout`).
 * É deliberado: sem isso o cache do React Query atravessaria a fronteira, e
 * dados de um cliente apareceriam no painel da plataforma — ou, pior, no
 * ambiente do cliente seguinte.
 */
export const suporte = {
  atual(): SessaoSuporte | null {
    const bruto = sessionStorage.getItem(SUPORTE_KEY);
    if (!bruto) return null;

    try {
      return JSON.parse(bruto) as SessaoSuporte;
    } catch {
      // Conteúdo corrompido não pode travar o app numa sessão de suporte fantasma.
      sessionStorage.removeItem(SUPORTE_KEY);
      return null;
    }
  },
  entrar(sessao: SessaoSuporte) {
    sessionStorage.setItem(SUPORTE_KEY, JSON.stringify(sessao));
  },
  sair() {
    sessionStorage.removeItem(SUPORTE_KEY);
  },
};

export const tokens = {
  // O token de suporte tem precedência: enquanto ele existe, TODA chamada sai
  // no contexto do cliente, e é isso que faz as telas existentes funcionarem
  // sem saber que estão sendo vistas por alguém de fora.
  access: () =>
    suporte.atual()?.token ??
    sessionStorage.getItem(ACCESS_KEY) ??
    localStorage.getItem(ACCESS_KEY),
  refresh: () => sessionStorage.getItem(REFRESH_KEY) ?? localStorage.getItem(REFRESH_KEY),
  salvar(accessToken: string, refreshToken: string, lembrar?: boolean) {
    const destino = lembrar === undefined ? lojaAtual() : lembrar ? localStorage : sessionStorage;
    const outra = destino === localStorage ? sessionStorage : localStorage;

    // Sem limpar a outra, um login "não lembrar" depois de um "lembrar" deixaria
    // o token antigo em localStorage e a sessão voltaria sozinha na próxima vez.
    outra.removeItem(ACCESS_KEY);
    outra.removeItem(REFRESH_KEY);

    destino.setItem(ACCESS_KEY, accessToken);
    destino.setItem(REFRESH_KEY, refreshToken);
  },
  limpar() {
    for (const loja of [localStorage, sessionStorage]) {
      loja.removeItem(ACCESS_KEY);
      loja.removeItem(REFRESH_KEY);
    }
    // Sair leva o acesso de suporte junto: sem isto o próximo login herdaria
    // uma sessão dentro de um cliente, sem passar por motivo nem auditoria.
    suporte.sair();
  },
};

/**
 * Renovação em voo, compartilhada DENTRO da aba.
 *
 * Uma tela costuma disparar várias consultas de uma vez. Se o token vencer, TODAS
 * levam 401 ao mesmo tempo — sem esta trava, cada uma chamaria `/refresh`, e como
 * a rotação invalida o token anterior, a segunda chamada em diante derrubaria a
 * sessão inteira por "reuso detectado". A primeira renova; as outras esperam.
 */
let renovacaoEmVoo: Promise<boolean> | null = null;

const TRAVA_RENOVACAO = 'mpa:renovar-sessao';

/**
 * A mesma exclusão mútua, agora ENTRE ABAS.
 *
 * Era o que faltava para o "Lembrar de mim" funcionar de verdade. Marcado, a
 * sessão vai para `localStorage`, que é COMPARTILHADO entre as abas — então
 * duas abas dividem um único refresh token e uma única sessão no servidor.
 * Quando o acesso vence, as duas renovam ao mesmo tempo: a primeira rotaciona,
 * a segunda reapresenta o token que acabou de morrer, e o servidor faz o que
 * deve fazer diante de um token reusado — revoga a sessão inteira.
 *
 * O sintoma é justamente "marquei lembrar e não lembrou": reabrir o navegador
 * restaurando várias abas do painel derruba a sessão na hora. Desmarcado nunca
 * acontecia, porque aí cada aba tem `sessionStorage` e sessão próprios.
 *
 * `navigator.locks` existe para exatamente isto e é liberado sozinho se a aba
 * morrer no meio. Onde não existir, fica a trava por aba — que é o que havia
 * antes, e é melhor do que não ter nenhuma.
 */
async function comTravaEntreAbas<T>(nome: string, fn: () => Promise<T>): Promise<T> {
  const locks = navigator.locks;
  if (!locks) return fn();

  // `await` achata o `Promise<Promise<T>>` que a assinatura do LockManager
  // produz quando o callback é assíncrono.
  return await locks.request(nome, fn);
}

async function renovarSessao(): Promise<boolean> {
  const tokenAntes = tokens.refresh();
  if (!tokenAntes) return false;

  renovacaoEmVoo ??= comTravaEntreAbas(TRAVA_RENOVACAO, async () => {
    /**
     * Relido DENTRO da trava, e comparado com o que esta aba viu antes de
     * esperar. Diferente significa que outra aba renovou enquanto esperávamos:
     * o token novo já está no storage, e gastar o antigo agora seria
     * exatamente o reuso que derruba a sessão. Não há o que fazer — já está
     * renovado.
     */
    const tokenAgora = tokens.refresh();
    if (!tokenAgora) return false;
    if (tokenAgora !== tokenAntes) return true;

    try {
      const resposta = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokenAgora }),
      });

      if (resposta.ok) {
        const dados = (await resposta.json()) as { accessToken: string; refreshToken: string };
        tokens.salvar(dados.accessToken, dados.refreshToken);
        return true;
      }

      /**
       * Só 401 e 403 apagam a sessão — são as respostas que dizem "este token
       * não vale mais". Um 500 ou um 502 do proxy dizem que o SERVIDOR falhou,
       * e deslogar alguém por instabilidade momentânea é perder a sessão que
       * continuava perfeitamente válida.
       */
      if (resposta.status === 401 || resposta.status === 403) tokens.limpar();
      return false;
    } catch {
      // Rede caiu no meio. O token continua bom; a próxima chamada tenta de novo.
      return false;
    }
  }).finally(() => {
    // Libera a próxima renovação só depois desta terminar.
    setTimeout(() => (renovacaoEmVoo = null), 0);
  });

  return renovacaoEmVoo;
}

interface Opcoes {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Envia FormData sem serializar (upload de planilha). */
  formData?: FormData;
  /** Não tenta renovar a sessão em 401 — usado pelo próprio login. */
  semRenovacao?: boolean;
}

export async function api<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  const executar = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    const access = tokens.access();
    if (access) headers.Authorization = `Bearer ${access}`;

    let body: BodyInit | undefined;
    if (opcoes.formData) {
      // Sem Content-Type: o browser precisa definir o boundary do multipart.
      body = opcoes.formData;
    } else if (opcoes.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opcoes.body);
    }

    return fetch(`/api${caminho}`, { method: opcoes.method ?? 'GET', headers, body });
  };

  let resposta = await executar();

  // O token de suporte vence em 15 minutos e o refresh NÃO o renova — de
  // propósito, para que a sessão de suporte não se prorrogue em silêncio. Tentar
  // renovar aqui devolveria um token normal e o Superadmin continuaria vendo o
  // ambiente do cliente com um token que não é mais de suporte, ou seja: sem
  // auditoria e sem a trava de leitura. O certo é encerrar e voltar ao painel.
  if (resposta.status === 401 && suporte.atual()) {
    suporte.sair();
    window.location.href = '/master?suporteExpirado=1';
    // A navegação é assíncrona; sem isto o resto da função seguiria rodando.
    await new Promise(() => {});
  }

  if (resposta.status === 401 && !opcoes.semRenovacao && tokens.refresh()) {
    if (await renovarSessao()) {
      resposta = await executar();
    }
  }

  if (resposta.status === 204) {
    return undefined as T;
  }

  const texto = await resposta.text();
  const dados: unknown = texto ? JSON.parse(texto) : null;

  if (!resposta.ok) {
    const codigo = (dados as { code?: unknown } | null)?.code;
    throw new ApiError(
      resposta.status,
      extrairMensagem(dados, resposta.status),
      extrairCampos(dados),
      typeof codigo === 'string' ? codigo : undefined,
    );
  }

  return dados as T;
}

/**
 * O backend fala em três formatos: exceção do Nest (`message` string), validação
 * do Zod (`errors[]`) e `message` como array. Traduzir aqui evita que cada tela
 * reimplemente a mesma cadeia de `??`.
 */
function extrairMensagem(dados: unknown, status: number): string {
  const d = dados as Record<string, unknown> | null;

  if (d && typeof d.message === 'string' && d.message !== 'Validation failed') {
    return d.message;
  }
  if (d && Array.isArray(d.message) && typeof d.message[0] === 'string') {
    return d.message[0];
  }

  const erros = d?.errors as { message?: string; path?: (string | number)[] }[] | undefined;
  if (Array.isArray(erros) && erros[0]?.message) {
    return erros[0].message;
  }

  if (status === 401) return 'Sessão expirada. Entre novamente.';
  if (status === 403) return 'Você não tem permissão para esta ação.';
  if (status >= 500) return 'Erro no servidor. Tente novamente em instantes.';
  return 'Não foi possível concluir a operação.';
}

function extrairCampos(dados: unknown): Record<string, string> | undefined {
  const erros = (dados as { errors?: { message?: string; path?: (string | number)[] }[] })?.errors;
  if (!Array.isArray(erros)) return undefined;

  const campos: Record<string, string> = {};
  for (const erro of erros) {
    const campo = erro.path?.join('.');
    if (campo && erro.message) campos[campo] = erro.message;
  }

  return Object.keys(campos).length ? campos : undefined;
}
